const MAX_SIZE = 800; // longest side in pixels; sharp enough for sharing later
const QUALITY = 0.75;  // JPEG quality: good balance between size and sharpness

/**
 * Resize and compress an image file to a JPEG blob.
 */
export async function compressImage(file) {
    const source = await loadImage(file);

    // scale down (never up) so the longest side is at most MAX_SIZE
    const scale = Math.min(1, MAX_SIZE / Math.max(source.width, source.height));
    const width = Math.round(source.width * scale);
    const height = Math.round(source.height * scale);

    const canvas = document.createElement('canvas');

    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d').drawImage(source, 0, 0, width, height);

    // ImageBitmaps hold decoded pixels in memory; release them explicitly
    source.close?.();

    // try WebP first; Safari can't encode WebP and silently returns a PNG instead
    let blob = await canvasToBlob(canvas, 'image/webp', QUALITY);

    if (blob.type !== 'image/webp') {
        blob = await canvasToBlob(canvas, 'image/jpeg', QUALITY);
    }

    // free the canvas memory right away (helps on older iPhones)
    canvas.width = 0;
    canvas.height = 0;

    return blob;
}

/**
 * Promise wrapper around canvas.toBlob().
 */
function canvasToBlob(canvas, type, quality) {
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (result) => (result ? resolve(result) : reject(new Error('Could not encode image'))),
            type,
            quality,
        );
    });
}

/**
 * Decode an image file, respecting the photo's orientation (EXIF).
 */
async function loadImage(file) {
    if ('createImageBitmap' in window) {
        try {
            return await createImageBitmap(file, { imageOrientation: 'from-image' });
        } catch {
            // fallback to an <img> element below
        }
    }

    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();

        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
        };

        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Unsupported image'));
        };

        img.src = url;
    });
}
